import React, {useEffect, useState} from 'react';
import FeedExportItem from "./FeedExportItem.js";
import {Button, Table} from "reactstrap";
import {apiErrorRaised, useApiErrorDispatch} from "../../context/api_error/ApiErrorIndex";
import {FeedExports} from "../../services/api/FeedExports";
import {TableFilters} from "../TableFilters/TableFilters";
import {useTableFiltersState} from "../../context/table_filters/TableFiltersIndex";
import {Link} from "react-router-dom";
import {PlaceholderTable} from "../PlaceholderTable/PlaceholderTable";
import {closeModal, openModal} from "../../context/modal/ModalActions";
import {useModalDispatch} from "../../context/modal/ModalContext";
import {ColumnSelectorModal, useColumnSelector} from "../ColumnSelector";
import useUrlFilters from "../../hooks/useUrlFilters";

// Define all columns with their properties
const TABLE_COLUMNS = [
  {id: 'id', name: 'Id', default: false},
  {id: 'name', name: 'Name', default: true},
  {id: 'platform', name: 'Platform', default: true},
  {id: 'format', name: 'Format', default: false},
  {id: 'number_of_products', name: 'Products (total)', default: false},
  {id: 'filename', name: 'Filename', default: false},
  {id: 'last_export', name: 'Last Export', default: false},
  {id: 'schedule', name: 'Schedule', default: false,},
  {id: 'settings', name: 'Settings', default: true, required: true},
  {id: 'actions', name: 'Actions', default: true, required: true},
];

const TABLE_ID = 'feed_exports';

const FeedExportList = ({companyId, projectId}) => {
  const dispatch = useApiErrorDispatch()
  const [isFiltersCollapsed, setFiltersCollapsed] = useState(true)
  const [isLoading, setLoading] = useState(false)
  const [feedExports, setFeedExports] = useState([])
  const tableFilters = useTableFiltersState()
  const modalDispatcher = useModalDispatch()
  const [isRequestExportLoading, setRequestExportLoading] = useState(false)
  
  // Track whether the URL has been processed
  const [urlProcessed, setUrlProcessed] = useState(false);
  
  // Use URL filters for managing filter state (this component doesn't have pagination)
  const urlFilters = useUrlFilters({
    setFiltersCollapsed,
    onUrlProcessed: () => setUrlProcessed(true)
  });

  const {
    modalOpen,
    toggleModal,
    visibleColumns,
    handleSaveColumns,
    isColumnVisible
  } = useColumnSelector(TABLE_ID, TABLE_COLUMNS);

  const handleRequestExportClick = () => {
    openModal(modalDispatcher, {
      title: 'Request export feeds',
      message: 'Do you want to generate all exports?',
      onConfirm: () => {
        setRequestExportLoading(true)
        FeedExports.requestExports(companyId, projectId,).then((r) => {
          setRequestExportLoading(false);
          if (r === undefined || r.status > 399) {
            apiErrorRaised(dispatch, r?.response)
          }
        });
        closeModal(modalDispatcher)
      },
      onCancel: () => closeModal(modalDispatcher),
      onBackdropClick: () => closeModal(modalDispatcher),
      buttonText: 'Yes'
    })
  }

  const toggleCollapsed = () => {
    setFiltersCollapsed(!isFiltersCollapsed)
  }

  useEffect(() => {
    // Skip API call until URL parameters are processed
    if (!urlProcessed) {
      return;
    }
    
    setLoading(true);
    FeedExports.index(companyId, projectId, {filters: tableFilters}).then((r) => {
      if (r?.status < 400) {
        setFeedExports(r.data);
      } else {
        apiErrorRaised(dispatch, r?.response)
      }
      setLoading(false);
    });
  }, [dispatch, tableFilters, companyId, projectId, urlProcessed]);


  // Helper to get column name by ID
  const getColumnName = (columnId) => {
    const column = TABLE_COLUMNS.find(col => col.id === columnId);
    return column ? column.name : columnId;
  };

  return (
    <>
      <div className={'view_header'}>
        <div className={'title'}>
          <h1>Feed Exports</h1>
        </div>
        <div className={'button_actions'}>
          <Button outline color="primary"
                  onClick={toggleCollapsed}>
            <span className="fa-filter fa me-1"/>
            Filter
          </Button>
          <Button outline color="primary" onClick={toggleModal}>
            <span className="fa-columns fa me-1"/>
            Modify Columns
          </Button>
          <Link to={`/admin/companies/${companyId}/projects/${projectId}/feed_exports/new`}>
            <Button color={'primary'} className={'me-1'}>New Feed Export</Button>
          </Link>
          <Button outline color="primary" className="btn-icon"
                  onClick={handleRequestExportClick}>
            {
              isRequestExportLoading ?
                <span className="spinner-border spinner-border-sm me-1 ms-1"/>
                :
                <span className="fa fa-file-export me-1"/>
            }
            Export All
          </Button>
        </div>
      </div>
      <TableFilters entity={'feed_export'} isCollapsed={isFiltersCollapsed}/>
      {
        isLoading === true ? <PlaceholderTable rows={20} firstEmpty={false} columns={4}/> :
          <Table className="align-items-center table-flush mt-3" responsive>
            <thead className="thead-light">
            <tr>
              {visibleColumns.map(columnId => (
                <th key={columnId} className={'text-center'}>
                  {getColumnName(columnId)}
                </th>
              ))}
            </tr>
            </thead>
            <tbody>
            {
              isLoading ?
                <tr>
                  <td><span className="spinner-border spinner-border-lg me-1"/></td>
                </tr> : feedExports && feedExports.map((feedExport, i) => {
                return (<FeedExportItem
                  key={i}
                  data={feedExport}
                  visibleColumns={visibleColumns}
                />);
              })
            }
            </tbody>
          </Table>
      }

      {/* Column selector modal */}
      <ColumnSelectorModal
        isOpen={modalOpen}
        toggle={toggleModal}
        tableId={TABLE_ID}
        availableColumns={TABLE_COLUMNS}
        onSave={handleSaveColumns}
      />
    </>
  );
}

export default FeedExportList;
